import { useEffect, useMemo, useRef, useState } from "react";
import { SERVER_URL } from "../../../constants"; // adjust path
import { fetchJsonAuth } from "../utils/http";

export function useStudentDisplayList(studentIds = [], token, enabled = true) {
  const ids = useMemo(
    () => (Array.isArray(studentIds) ? studentIds.filter(Boolean) : []),
    [studentIds]
  );

  const cacheRef = useRef(new Map()); // id -> {id,name}
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);

  const invalidate = (idsToClear = []) => {
    idsToClear.forEach((id) => cacheRef.current.delete(id));
    setStudents((prev) => prev.filter((s) => !idsToClear.includes(s.id)));
  };

  useEffect(() => {
    if (!enabled) return;

    if (!ids.length) {
      setStudents([]);
      return;
    }

    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        const missing = ids.filter((id) => !cacheRef.current.has(id));

        if (missing.length) {
          let fetched = [];
          try {
            fetched = await fetchJsonAuth(`${SERVER_URL}api/students/batch`, token, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
              },
              body: JSON.stringify({ ids: Array.from(new Set(missing)) }),
            });
          } catch (e) {
            // If batch fails, fall back to placeholders (keeps UI resilient)
            fetched = [];
          }

          const byId = new Map((Array.isArray(fetched) ? fetched : []).map((s) => [s.id, s]));

          for (const id of missing) {
            const s = byId.get(id);

            const first =
              s?.studentName ?? s?.student_name ?? s?.firstname ?? s?.first_name ?? s?.forename ?? "";
            const last =
              s?.studentSurname ?? s?.student_surname ?? s?.lastname ?? s?.last_name ?? s?.surname ?? "";

            const name = `${first} ${last}`.trim() || `Student ${id}`;
            cacheRef.current.set(id, { id, name });
          }
        }

        const out = ids.map((id) => cacheRef.current.get(id));
        if (!cancelled) setStudents(out);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [ids, token, enabled]);

  return { students, loading, invalidate };
}
